import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Route, useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { userDataSelector } from '@selectors/global';
import { authenticateWithUserService } from '@actions/user';
import { Loading } from '@components/PageComponents';

/**
 * Show a loading spinner until user is authenticated with Auth0 and there is user data in Redux
 * If user is not authenticated with Auth0, redirect to login page
 * @param {*} props - unknown props, passed to child component
 * @returns {React.Component} - Route component
 */
const AuthorizedRoute = (props) => {
  const auth0 = useAuth0();
  const userData = useSelector(userDataSelector);
  const authenticatedWithUserService = !!userData.get('id');
  const history = useHistory();
  const dispatch = useDispatch();

  const { render, ...rest } = props;

  // If the user is authenticated with Auth0 but there is no user data in Redux, fetch the user data
  useEffect(() => {
    async function getTokenFromUserService() {
      try {
        const token = await auth0.getAccessTokenSilently();
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp < Date.now() / 1000) {
          throw 'Token expired';
        }
        await dispatch(
          authenticateWithUserService({
            token,
          }),
        );
      } catch (error) {
        const message = `Cannot log in to user service for ${
          auth0?.user?.office?.display_name
        } - ${error?.message?.toLowerCase() || error}}`;
        console.error(message);

        if (error?.message === 'External interaction required') {
          history.push('/login');
        }
      }
    }

    if (auth0.isAuthenticated && !authenticatedWithUserService) {
      getTokenFromUserService();
    }
  }, [
    auth0.isAuthenticated,
    auth0.getAccessTokenSilently,
    authenticatedWithUserService,
    dispatch,
    history,
  ]);

  // If the user is not authenticated with Auth0, redirect to login page
  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      history.push('/login');
    }
  }, [auth0.isLoading, auth0.isAuthenticated, history]);

  return (
    <Route
      render={
        auth0.isAuthenticated && authenticatedWithUserService
          ? render
          : () => (
              <Center>
                <Loading />
              </Center>
            )
      }
      {...rest}
    />
  );
};

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 67px);
`;

AuthorizedRoute.propTypes = {
  render: PropTypes.func,
};

export default AuthorizedRoute;
